import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import mapboxgl from "mapbox-gl";

/**
 * Create-time tile picker. Mirrors the on-map "+" expansion UX of
 * {@link ProjectExpandButtons}, but operates on a purely client-side set of
 * selected tiles (no project exists yet). The user places one square and then
 * grows the area north/south/east/west; on submit the wizard creates the base
 * square and fires one /expand call per extra tile.
 *
 * Tiles are addressed as origin-relative {col,row} offsets, where the base
 * square is (0,0), north is +row and east is +col — the same grid the backend
 * expansion uses.
 */
interface Props {
  map:
    | {
        getCanvas(): HTMLCanvasElement;
        project(lngLat: [number, number]): { x: number; y: number };
        on(e: string, fn: () => void): void;
        off(e: string, fn: () => void): void;
      }
    | null;
  originLng: number;
  originLat: number;
  tileWidthDeg: number;
  tileHeightDeg: number;
  /** Currently selected tiles, including the base square at (0,0). */
  tiles: { col: number; row: number }[];
  onAdd: (col: number, row: number) => void;
  onRemove: (col: number, row: number) => void;
}

interface AddAnchor {
  lngLat: [number, number];
  col: number;
  row: number;
  key: string;
  tileBounds: { tL: number; tB: number; tR: number; tT: number };
}

const PLUS_SVG =
  '<svg viewBox="0 0 24 24" width="22" height="22"><path d="M19 13H13V19H11V13H5V11H11V5H13V11H19V13Z" fill="rgba(0,212,173,0.95)"/></svg>';
const MINUS_SVG =
  '<svg viewBox="0 0 24 24" width="20" height="20"><path d="M19 13H5V11H19V13Z" fill="rgba(255,255,255,0.95)"/></svg>';

const CreateProjectTileSelector: React.FC<Props> = ({
  map,
  originLng,
  originLat,
  tileWidthDeg,
  tileHeightDeg,
  tiles,
  onAdd,
  onRemove,
}) => {
  const [hoveredPreviewPoints, setHoveredPreviewPoints] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const anchorsRef = useRef<AddAnchor[]>([]);
  const hoveredKeyRef = useRef<string | null>(null);

  // Keep the latest callbacks/geometry in refs so the marker click handlers
  // (created once per tiles change) always see current values.
  const onAddRef = useRef(onAdd);
  const onRemoveRef = useRef(onRemove);
  useEffect(() => {
    onAddRef.current = onAdd;
    onRemoveRef.current = onRemove;
  }, [onAdd, onRemove]);

  const computeAddAnchors = useCallback((): AddAnchor[] => {
    const occupied = new Set(tiles.map((t) => `${t.col},${t.row}`));
    const isOccupied = (col: number, row: number) => occupied.has(`${col},${row}`);

    const result: AddAnchor[] = [];
    const seen = new Set<string>();
    // Anchor the "+" on the shared boundary edge between the existing tile and
    // the empty neighbour it would add (not out at the new tile's centre).
    const push = (col: number, row: number, lngLat: [number, number]) => {
      const key = `${col},${row}`;
      if (seen.has(key) || isOccupied(col, row)) return;
      seen.add(key);
      const tL = originLng + col * tileWidthDeg;
      const tB = originLat + row * tileHeightDeg;
      result.push({ lngLat, col, row, key, tileBounds: { tL, tB, tR: tL + tileWidthDeg, tT: tB + tileHeightDeg } });
    };

    for (const { col, row } of tiles) {
      const cellLeft = originLng + col * tileWidthDeg;
      const cellBottom = originLat + row * tileHeightDeg;
      const midX = cellLeft + tileWidthDeg / 2;
      const midY = cellBottom + tileHeightDeg / 2;
      push(col, row + 1, [midX, cellBottom + tileHeightDeg]); // north edge
      push(col, row - 1, [midX, cellBottom]); // south edge
      push(col + 1, row, [cellLeft + tileWidthDeg, midY]); // east edge
      push(col - 1, row, [cellLeft, midY]); // west edge
    }
    return result;
  }, [tiles, originLng, originLat, tileWidthDeg, tileHeightDeg]);

  const updateHoverPreview = useCallback(() => {
    const key = hoveredKeyRef.current;
    if (!map || !key) {
      setHoveredPreviewPoints(null);
      return;
    }
    const anchor = anchorsRef.current.find((a) => a.key === key);
    if (!anchor) {
      setHoveredPreviewPoints(null);
      return;
    }
    const { tL, tB, tR, tT } = anchor.tileBounds;
    const sw = map.project([tL, tB]);
    const se = map.project([tR, tB]);
    const ne = map.project([tR, tT]);
    const nw = map.project([tL, tT]);
    setHoveredPreviewPoints(`${sw.x},${sw.y} ${se.x},${se.y} ${ne.x},${ne.y} ${nw.x},${nw.y}`);
  }, [map]);

  // Recreate flat-on-map markers whenever the tiles or map change.
  useEffect(() => {
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];
    if (!map) return;

    const addAnchors = computeAddAnchors();
    anchorsRef.current = addAnchors;

    // "+" markers — add a tile in that position.
    addAnchors.forEach((anchor) => {
      const el = document.createElement("div");
      el.title = "Add tile";
      el.style.cssText = [
        "width:36px",
        "height:36px",
        "border-radius:50%",
        "background:rgba(0,0,0,0.25)",
        "border:2px dashed rgba(0,212,173,0.85)",
        "display:flex",
        "align-items:center",
        "justify-content:center",
        "cursor:pointer",
        "backdrop-filter:blur(2px)",
      ].join(";");
      el.innerHTML = PLUS_SVG;
      el.addEventListener("click", (e) => {
        e.stopPropagation();
        // Drop the hover preview so the dashed highlight doesn't linger over the
        // tile that was just added.
        hoveredKeyRef.current = null;
        setHoveredPreviewPoints(null);
        onAddRef.current(anchor.col, anchor.row);
      });
      el.addEventListener("mouseenter", () => {
        hoveredKeyRef.current = anchor.key;
        updateHoverPreview();
      });
      el.addEventListener("mouseleave", () => {
        hoveredKeyRef.current = null;
        setHoveredPreviewPoints(null);
      });
      const marker = new mapboxgl.Marker({
        element: el,
        pitchAlignment: "map",
        rotationAlignment: "map",
        anchor: "center",
      })
        .setLngLat(anchor.lngLat)
        .addTo(map as unknown as mapboxgl.Map);
      markersRef.current.push(marker);
    });

    // "−" markers — remove an added tile (everything except the base square).
    tiles
      .filter((t) => !(t.col === 0 && t.row === 0))
      .forEach((t) => {
        const tL = originLng + t.col * tileWidthDeg;
        const tB = originLat + t.row * tileHeightDeg;
        const center: [number, number] = [tL + tileWidthDeg / 2, tB + tileHeightDeg / 2];
        const el = document.createElement("div");
        el.title = "Remove tile";
        el.style.cssText = [
          "width:30px",
          "height:30px",
          "border-radius:50%",
          "background:rgba(239,68,68,0.85)",
          "border:2px solid rgba(255,255,255,0.85)",
          "display:flex",
          "align-items:center",
          "justify-content:center",
          "cursor:pointer",
        ].join(";");
        el.innerHTML = MINUS_SVG;
        el.addEventListener("click", (e) => {
          e.stopPropagation();
          onRemoveRef.current(t.col, t.row);
        });
        const marker = new mapboxgl.Marker({
          element: el,
          pitchAlignment: "map",
          rotationAlignment: "map",
          anchor: "center",
        })
          .setLngLat(center)
          .addTo(map as unknown as mapboxgl.Map);
        markersRef.current.push(marker);
      });

    return () => {
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];
    };
  }, [map, computeAddAnchors, updateHoverPreview, tiles, originLng, originLat, tileWidthDeg, tileHeightDeg]);

  // Keep the hover preview polygon in sync as the map moves/zooms.
  useEffect(() => {
    if (!map) return;
    map.on("move", updateHoverPreview);
    map.on("zoom", updateHoverPreview);
    return () => {
      map.off("move", updateHoverPreview);
      map.off("zoom", updateHoverPreview);
    };
  }, [map, updateHoverPreview]);

  // Attach an overlay container to the map canvas parent for the hover preview.
  useEffect(() => {
    if (!map) return;
    const parent = map.getCanvas().parentElement;
    if (!parent) return;
    if (!containerRef.current) {
      const div = document.createElement("div");
      div.style.cssText =
        "position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:10;";
      parent.appendChild(div);
      containerRef.current = div;
    }
    return () => {
      if (containerRef.current && containerRef.current.parentElement) {
        containerRef.current.parentElement.removeChild(containerRef.current);
        containerRef.current = null;
      }
    };
  }, [map]);

  if (!containerRef.current) return null;

  return createPortal(
    <svg
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
      }}
    >
      {hoveredPreviewPoints && (
        <polygon
          points={hoveredPreviewPoints}
          fill="rgba(0,212,173,0.12)"
          stroke="rgba(0,212,173,0.8)"
          strokeWidth={2}
          strokeDasharray="7 4"
        />
      )}
    </svg>,
    containerRef.current
  );
};

export default CreateProjectTileSelector;
